import {Injectable} from '@angular/core';
import {BiometryVendor, GetAuthSessionDataResponse, Organization} from '@npck/auth-frontend-api';
import {LocalizationService, parseJwt, SentryService} from '@npck/libs/commons';
import {OauthResponse} from '@npck/oauth-service-api';
import {SessionStorageService} from 'ngx-webstorage';
import {LOCALE_KEY} from './browser-storage.keys';

const AUTH_SESSION_DATA = 'auth-session-data';
const AUTH_SESSION_TOKEN = 'auth-session-token';
const OAUTH_SESSION_ID = 'oauth-session-id';
const OAUTH_CONSENT_DATA = 'oauth-consent-data';
const OAUTH_ORGANIZATION_DATA = 'oauth-organization-data';
const LIVENESS_QR_IIN = 'liveness-qr-iin';
const FIRST_FACTOR_TOKEN = 'first-factor-token';
const SECOND_FACTOR_TOKEN = 'second-factor-token';
const ORGANIZATION_TOKEN = 'organization-token';
const BIOMETRY_VENDOR = 'biometry-vendor';
const LIVENESS_QR_ID = 'liveness-qr-id';
const QR_BLOCK_TIME_MINUTES = 'qr-block-time-minutes';
const RESET_ESIGN = 'reset-esign';

export const OAUTH_DEVICE_ID = 'oauth-device-id';
export const SMS_CODE_TIMER_START_TIME = 'login-timer-start-time';

type SessionData = GetAuthSessionDataResponse;

@Injectable({
    providedIn: 'root',
})
export class AuthHolderService {
    authSessionData: SessionData;
    deviceKey: string;
    firstFactorToken: string;
    secondFactorToken: string;
    organizationToken: string;
    authSessionToken: string;
    oauthSessionId: string;
    consentData: OauthResponse;
    organization: Organization;
    livenessQrIin: string;
    biometryVendor: BiometryVendor;
    livenessQrId: string;
    resetEsign: boolean;
    private qrBlockTimeMinutes: number;

    constructor(
        private sessionStorageService: SessionStorageService,
        private localizationService: LocalizationService,
        private sentryService: SentryService,
    ) {
        this.authSessionData = this.retrieveOauthSessionData();
        this.deviceKey = this.retrieveDeviceKey();
        this.firstFactorToken = this.retrieveFirstFactorToken();
        this.secondFactorToken = this.retrieveSecondFactorToken();
        this.organizationToken = this.retrieveOrganizationToken();
        this.authSessionToken = this.retrieveAuthSessionToken();
        this.oauthSessionId = this.retrieveOauthSessionId();
        this.consentData = this.retrieveOauthConsentData();
        this.organization = this.retrieveOrganization();
        this.livenessQrIin = this.retrieveLivenessQrIin();
        this.biometryVendor = this.authSessionData?.biometryVendor || this.retrieveBiometryVendor();
        this.livenessQrId = this.retrieveLivenessQrId();
        if (this.authSessionData) {
            this.sentryService.setUser(this.authSessionData.iinEncrypted);
        }
        this.qrBlockTimeMinutes = this.retrieveQrBlockTimeMinutes();
        this.resetEsign = this.retrieveResetEsign();
    }

    get skipLiveness(): boolean {
        const oauthData = this.authSessionData;
        return oauthData?.skipLivenessPhonesEncrypted?.includes(oauthData?.phoneEncrypted)
            || oauthData?.skipLivenessIinsEncrypted?.includes(oauthData?.iinEncrypted)
            || oauthData?.skipLivenessClientIdsEncrypted?.includes(oauthData?.clientIdEncrypted);
    }

    get iin(): string {
        return this.authSessionData?.iin || this.livenessQrIin;
    }

    get externalOrgNameRu(): string {
        return this.authSessionData?.externalOrgNameRu;
    }

    get externalOrgNameKk(): string {
        return this.authSessionData?.externalOrgNameKk;
    }

    get blockTimeMinutes(): number {
        return this.authSessionData?.blockTimeMinutes || this.qrBlockTimeMinutes;
    }

    get organizations(): Array<Organization> {
        return this.authSessionData?.organizations;
    }

    get hasEresidencyScope(): boolean {
        return !!this.authSessionData?.scopes?.includes('e_residency');
    }

    storeOauthSessionData(oauthData: GetAuthSessionDataResponse): void {
        this.authSessionData = oauthData;
        if (oauthData.language) {
            this.storeLocale(oauthData.language.toLowerCase());
            this.localizationService.set(this.localizationService.getLanguage(oauthData.language));
        }
        if (oauthData.biometryVendor) {
            this.storeBiometryVendor(oauthData.biometryVendor);
        }
        this.sessionStorageService.store(AUTH_SESSION_DATA, JSON.stringify(this.authSessionData));
        this.sentryService.setUser(this.authSessionData.iinEncrypted);
    }

    storeQrBlockTime(blockTime: number): void {
        this.qrBlockTimeMinutes = blockTime;
        this.sessionStorageService.store(QR_BLOCK_TIME_MINUTES, blockTime);
    }

    storeDeviceKey(deviceKey: string): void {
        this.deviceKey = deviceKey;
        this.sessionStorageService.store(OAUTH_DEVICE_ID, deviceKey);
    }

    storeLocale(language: string): void {
        this.sessionStorageService.store(LOCALE_KEY, language);
    }

    storeOauthSessionId(sessionId: string): void {
        this.oauthSessionId = sessionId;
        this.sessionStorageService.store(OAUTH_SESSION_ID, sessionId);
    }

    storeFirstFactorToken(firstFactorToken: string): void {
        this.firstFactorToken = firstFactorToken;
        this.sessionStorageService.store(FIRST_FACTOR_TOKEN, firstFactorToken);
    }

    storeSecondFactorToken(secondFactorToken: string): void {
        this.secondFactorToken = secondFactorToken;
        this.sessionStorageService.store(SECOND_FACTOR_TOKEN, secondFactorToken);
    }

    storeOrganizationToken(organizationToken: string): void {
        this.organizationToken = organizationToken;
        this.sessionStorageService.store(ORGANIZATION_TOKEN, organizationToken);
    }

    storeAuthSessionToken(authSessionToken: string): void {
        this.authSessionToken = authSessionToken;
        const claims = parseJwt(authSessionToken);
        this.storeOauthSessionId(claims.sid);
        this.sessionStorageService.store(AUTH_SESSION_TOKEN, authSessionToken);
    }

    storeBiometryVendor(biometryVendor: BiometryVendor): void {
        this.biometryVendor = biometryVendor;
        this.sessionStorageService.store(BIOMETRY_VENDOR, biometryVendor);
    }

    storeOauthConsentData(consentData: OauthResponse): void {
        this.consentData = consentData;
        this.sessionStorageService.store(OAUTH_CONSENT_DATA, JSON.stringify(consentData));
    }

    storeLivenessQrIin(iin: string): void {
        this.livenessQrIin = iin;
        this.sessionStorageService.store(LIVENESS_QR_IIN, iin);
    }

    storeOrganization(organization: Organization): void {
        this.organization = organization;
        this.sessionStorageService.store(OAUTH_ORGANIZATION_DATA, JSON.stringify(organization));
    }

    storeLivenessQrId(livenessQrId: string): void {
        this.livenessQrId = livenessQrId;
        this.sessionStorageService.store(LIVENESS_QR_ID, livenessQrId);
    }

    storeResetEsign(): void {
        this.resetEsign = true;
        this.sessionStorageService.store(RESET_ESIGN, this.resetEsign);
    }

    getActiveAuthToken(): string {
        return this.organizationToken || this.secondFactorToken || this.firstFactorToken;
    }

    getCurrentAuthSessionId(): string | null {
        if (this.oauthSessionId) {
            return this.oauthSessionId;
        }
        const token = this.getAnyToken();
        if (!token) {
            return null;
        }
        try {
            return parseJwt(token)?.sid ?? null;
        } catch {
            return null;
        }
    }

    getAnyToken(): string {
        return this.organizationToken || this.secondFactorToken || this.firstFactorToken || this.authSessionToken;
    }

    hasAuthSession(): boolean {
        return !!this.authSessionData || !!this.livenessQrId;
    }

    clear(): void {
        this.authSessionData = null;
        this.deviceKey = null;
        this.firstFactorToken = null;
        this.secondFactorToken = null;
        this.organizationToken = null;
        this.authSessionToken = null;
        this.consentData = null;
        this.oauthSessionId = null;
        this.sentryService.resetUser();
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('ngx-webstorage')) {
                sessionStorage.removeItem(key);
            }
        });
    }

    private persistSessionData(): void {
        this.sessionStorageService.store(AUTH_SESSION_DATA, JSON.stringify(this.authSessionData));
    }

    private retrieveLivenessQrId(): string {
        return this.sessionStorageService.retrieve(LIVENESS_QR_ID);
    }

    private retrieveOrganization(): Organization {
        return JSON.parse(this.sessionStorageService.retrieve(OAUTH_ORGANIZATION_DATA));
    }

    private retrieveLivenessQrIin(): string {
        return this.sessionStorageService.retrieve(LIVENESS_QR_IIN);
    }

    private retrieveBiometryVendor(): BiometryVendor {
        return this.sessionStorageService.retrieve(BIOMETRY_VENDOR);
    }

    private retrieveOauthConsentData(): OauthResponse {
        return JSON.parse(this.sessionStorageService.retrieve(OAUTH_CONSENT_DATA));
    }

    private retrieveOrganizationToken(): string {
        return this.sessionStorageService.retrieve(ORGANIZATION_TOKEN);
    }

    private retrieveAuthSessionToken(): string {
        return this.sessionStorageService.retrieve(AUTH_SESSION_TOKEN);
    }

    private retrieveSecondFactorToken(): string {
        return this.sessionStorageService.retrieve(SECOND_FACTOR_TOKEN);
    }

    private retrieveFirstFactorToken(): string {
        return this.sessionStorageService.retrieve(FIRST_FACTOR_TOKEN);
    }

    private retrieveOauthSessionId(): string {
        return this.sessionStorageService.retrieve(OAUTH_SESSION_ID);
    }

    private retrieveDeviceKey(): string {
        return this.sessionStorageService.retrieve(OAUTH_DEVICE_ID);
    }

    private retrieveOauthSessionData(): GetAuthSessionDataResponse {
        return JSON.parse(this.sessionStorageService.retrieve(AUTH_SESSION_DATA));
    }

    private retrieveQrBlockTimeMinutes(): number {
        return this.sessionStorageService.retrieve(QR_BLOCK_TIME_MINUTES);
    }

    private retrieveResetEsign(): boolean {
        return this.sessionStorageService.retrieve(RESET_ESIGN);
    }
}
